import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiDownload, FiEye, FiSearch } from "react-icons/fi";
import api from "../services/api";
import IncentiveSummary from "../components/dashboard/IncentiveSummary";

const PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

const IncentiveA = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [serviceFee, setServiceFee] = useState(0);
  const [statusOptions, setStatusOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("requested_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [exportError, setExportError] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState("");
  const [isBatchOpen, setIsBatchOpen] = useState(false);
  const [batchFile, setBatchFile] = useState(null);
  const [batchFileName, setBatchFileName] = useState("");
  const [batchError, setBatchError] = useState("");
  const [batchSuccess, setBatchSuccess] = useState("");
  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const [isBatchSuccessOpen, setIsBatchSuccessOpen] = useState(false);
  const navigate = useNavigate();

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchAdminIncentives = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/incentives/admin", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            status_id: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load incentive admin data.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
        setSummary(data.summary || null);
        setServiceFee(data.service_fee || 0);
        setStatusOptions(data.status_options || []);
      } catch (error) {
        setLoadError("Failed to load incentive admin data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdminIncentives();
  }, [query, dateFrom, dateTo, statusFilter, sortBy, sortDir, page, perPage]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const handleExportExcel = async () => {
    if (!dateFrom || !dateTo) {
      setExportError("Request Date From and Request Date To are required for export.");
      return;
    }

    setExportError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setExportError("Missing access token.");
        return;
      }

      const response = await api.get("/incentives/admin/export", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          search: query,
          date_from: dateFrom,
          date_to: dateTo,
          status_id: statusFilter || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.ms-excel",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `incentive-admin-export-${dateFrom}-to-${dateTo}.csv`;
      link.href = url;
      link.download = match?.[1] || fallbackName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError("Failed to export incentive data.");
    }
  };

  const handleBatchUpload = async (event) => {
    event.preventDefault();
    if (!batchFile) {
      setBatchError("Please upload a CSV file.");
      return;
    }

    setBatchError("");
    setBatchSuccess("");
    setIsBatchSubmitting(true);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setBatchError("Missing access token.");
        return;
      }

      const formData = new FormData();
      formData.append("file", batchFile);

      const response = await api.post("/incentives/admin/batch-payouts", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status !== "success") {
        setBatchError(response.data?.message || "Failed to upload batch payout.");
        return;
      }

      setBatchSuccess(response.data?.message || "Batch payout upload queued.");
      setBatchFile(null);
      setBatchFileName("");
      setIsBatchOpen(false);
      setIsBatchSuccessOpen(true);
    } catch (error) {
      setBatchError("Failed to upload batch payout.");
    } finally {
      setIsBatchSubmitting(false);
    }
  };

  const handleView = async (id) => {
    setIsViewOpen(true);
    setViewError("");
    setViewData(null);
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setViewError("Missing access token.");
        return;
      }

      const response = await api.get(`/incentives/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setViewError("Failed to load payout details.");
        return;
      }

      setViewData(response.data?.data || null);
    } catch (error) {
      setViewError("Failed to load payout details.");
    }
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return date.toLocaleString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
      // hour: "2-digit",
      // minute: "2-digit",
      // hour12: true,
    });
  };

  const formatAmount = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
      return "₱ 0.00";
    }
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace("₱", "₱ ");
  };

  const getStatusClass = (value) => {
    const normalized = String(value || "").toLowerCase();
    if (normalized === "paid") {
      return "bg-emerald-100 text-emerald-700";
    }
    if (normalized === "cancelled" || normalized === "canceled") {
      return "bg-rose-100 text-rose-700";
    }
    if (normalized === "rejected") {
      return "bg-red-100 text-red-600";
    }
    if (normalized === "for approval") {
      return "bg-amber-100 text-amber-700";
    }
    return "bg-slate-100 text-slate-600";
  };

  const renderLevelList = (levels) => {
    if (!levels) {
      return null;
    }
    return (
      <div className="text-xs text-slate-600">
        <span className="font-semibold text-slate-700">Levels</span>
        <div className="mt-1 space-y-0.5">
          {[1, 2, 3, 4, 5].map((level) => (
            <div key={level}>
              {level} - {formatAmount(levels[level] || 0)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  const totalSummary = summary?.total_amount || 0;
  const releasedSummary = summary?.released_amount || 0;
  const requestedSummary = summary?.requested_amount || 0;
  const availableSummary = summary?.available_amount || 0;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          Incentive Admin
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          Incentive Admin Overview
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Monitor payout requests, approve releases, and upload payout batches.
        </p>
        <p className="mt-2 text-sm text-slate-500">
          Includes member payout bank information: bank name, account name, and account number.
        </p>
      </div>

      <IncentiveSummary />
      {/*
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-900">
          <span className="text-xl">💰</span>
          Incentive Summary
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-700">
              Total
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {formatAmount(totalSummary)}
            </p>
            {renderLevelList(summary?.levels?.total)}
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Released
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {formatAmount(releasedSummary)}
            </p>
            {renderLevelList(summary?.levels?.released)}
          </div>
          <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-violet-700">
              Requested
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {formatAmount(requestedSummary)}
            </p>
            {renderLevelList(summary?.levels?.requested)}
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">
              For Request
            </p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">
              {formatAmount(availableSummary)}
            </p>
            {renderLevelList(summary?.levels?.available)}
          </div>
        </div>
      </div>
      */}

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <span className="text-xl">🕒</span>
          Payout History
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </form>
          <div className="flex items-center gap-3 text-sm text-slate-500">
            <span>Display</span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            className="min-w-[180px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">All Status</option>
            {statusOptions.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Request Date From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Request Date To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 lg:ml-auto">
            <button
              type="button"
              onClick={() => setIsBatchOpen(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-[var(--brand-orange)] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
            >
              Batch Upload
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300"
            >
              <FiDownload />
              Export Excel
            </button>
          </div>
        </div>

        {exportError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {exportError}
          </div>
        )}

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1200px] w-full text-left text-sm text-slate-600">
              <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
                <tr>
                  {[
                    { key: "requested_at", label: "Date" },
                    { key: "request_id", label: "Request ID" },
                    { key: "username", label: "Username" },
                    { key: "referral_code", label: "Referral Code" },
                    { key: "status", label: "Status" },
                    { key: "amount", label: "Amount" },
                    { key: "service_fee", label: "Service Fee" },
                    { key: "net_amount", label: "Net Amount" },
                    { key: "paid_at", label: "Payment Date" },
                    { key: "bank_reference_no", label: "Bank Reference No." },
                    { key: "bank_name", label: "Bank Name" },
                    { key: "bank_account_name", label: "Bank Account Name" },
                    { key: "bank_account_number", label: "Bank Account Number" },
                  ].map((column) => (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      className="cursor-pointer px-4 py-3 text-xs font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {sortBy === column.key && (
                          <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={14}>
                      Loading payout history...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={14}>
                      No payout history available.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-4 py-3">{formatDateTime(row.requested_at)}</td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {row.request_id || "-"}
                      </td>
                      <td className="px-4 py-3">{row.username || "-"}</td>
                      <td className="px-4 py-3">{row.referral_code || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusClass(
                            row.status_name
                          )}`}
                        >
                          {row.status_name || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {formatAmount(row.amount)}
                      </td>
                      <td className="px-4 py-3">{formatAmount(row.service_fee)}</td>
                      <td className="px-4 py-3">{formatAmount(row.net_amount)}</td>
                      <td className="px-4 py-3">{formatDateTime(row.paid_at)}</td>
                      <td className="px-4 py-3">{row.bank_reference_no || "-"}</td>
                      <td className="px-4 py-3">{row.bank_name || "-"}</td>
                      <td className="px-4 py-3">{row.bank_account_name || "-"}</td>
                      <td className="px-4 py-3">{row.bank_account_number || "-"}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleView(row.id)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        >
                          <FiEye />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isBatchOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Batch Upload
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Upload Payout Batch
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Upload a CSV with Referral ID, Request ID, Amount, Service Fee,
                  Net Incentive, Date of Release/Date of Payment (YYYY-MM-DD or
                  YYYY-MM-DD HH:MM:SS), and Bank Reference No.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {batchError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {batchError}
              </div>
            )}
            {batchSuccess && (
              <div className="mt-4 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-600">
                {batchSuccess}
              </div>
            )}

            <form onSubmit={handleBatchUpload} className="mt-6 space-y-4">
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                <p className="font-semibold text-slate-700">
                  {batchFileName || "Choose CSV file"}
                </p>
                <input
                  type="file"
                  accept=".csv"
                  className="mt-3 block w-full text-sm text-slate-500"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    setBatchFile(file || null);
                    setBatchFileName(file?.name || "");
                  }}
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsBatchOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isBatchSubmitting}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isBatchSubmitting ? "Uploading..." : "Upload CSV"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isBatchSuccessOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-500">
                  Success
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Payout Batch Uploaded
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Your payout batch upload is queued. You can visit the batch
                  payout history to review progress and failures.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsBatchSuccessOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsBatchSuccessOpen(false)}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
              >
                Got it
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsBatchSuccessOpen(false);
                  navigate("/batch-payout-history");
                }}
                className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Go to Batch Payout History
              </button>
            </div>
          </div>
        </div>
      )}

      {isViewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  Incentive
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Payout Request Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Review the full details for this payout request.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {viewError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {viewError}
              </div>
            )}

            {!viewError && !viewData && (
              <p className="mt-6 text-sm text-slate-400">Loading details...</p>
            )}

            {viewData && (
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                {[
                  { label: "Request ID", value: viewData.request_id },
                  { label: "Username", value: viewData.username },
                  { label: "Referral Code", value: viewData.referral_code },
                  { label: "Status", value: viewData.status_name },
                  { label: "Requested Date", value: formatDateTime(viewData.requested_at) },
                  { label: "Amount", value: formatAmount(viewData.amount) },
                  { label: "Service Fee", value: formatAmount(viewData.service_fee) },
                  { label: "Net Amount", value: formatAmount(viewData.net_amount) },
                  { label: "Payment Date", value: formatDateTime(viewData.paid_at) },
                  { label: "Bank Reference No.", value: viewData.bank_reference_no },
                  { label: "Bank Name", value: viewData.bank_name },
                  { label: "Bank Account Name", value: viewData.bank_account_name },
                  { label: "Bank Account Number", value: viewData.bank_account_number },
                  { label: "Remarks", value: viewData.remarks },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                  >
                    <p className="text-xs uppercase tracking-wide text-slate-400">
                      {item.label}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-700">
                      {item.value || "-"}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncentiveA;
